/** Deja solo dígitos y el dígito verificador (K), sin puntos, guion ni espacios. */
export const cleanRut = (rut: string): string => rut.replace(/[.\-\s]/g, '').toUpperCase();

const splitRut = (rut: string): { body: string; checkDigit: string } | null => {
  // El dominio staff guarda los RUT paleteados a 11 ("016901234-5"): el cero a
  // la izquierda es de la base, no del RUT.
  const clean = cleanRut(rut).replace(/^0+/, '');
  if (!/^\d{7,8}[\dK]$/.test(clean)) return null;
  return { body: clean.slice(0, -1), checkDigit: clean.slice(-1) };
};

/** Valida forma y dígito verificador (módulo 11). */
export const isValidRut = (rut: string): boolean => {
  const parts = splitRut(rut);
  if (!parts) return false;

  let sum = 0;
  let multiplier = 2;
  for (let i = parts.body.length - 1; i >= 0; i--) {
    sum += Number(parts.body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const rest = 11 - (sum % 11);
  const expected = rest === 11 ? '0' : rest === 10 ? 'K' : String(rest);
  return expected === parts.checkDigit;
};

/** "11.222.333-4" -> "11222333-4": el BFF exige cuerpo+DV con guion, sin puntos. */
export const toBffRut = (rut: string): string => {
  const parts = splitRut(rut);
  if (parts) return `${parts.body}-${parts.checkDigit}`;
  const clean = cleanRut(rut);
  return clean ? `${clean.slice(0, -1)}-${clean.slice(-1)}` : '';
};

/** "123456789" → "12.345.678-9". Devuelve el valor original si no es un RUT válido. */
export const formatRut = (rut: string): string => {
  const parts = splitRut(rut);
  if (!parts) return rut;
  const body = parts.body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${body}-${parts.checkDigit}`;
};

const MAX_RUT_RAW_LENGTH = 9;

export const formatRutInput = (value: string): string => {
  const clean = cleanRut(value)
    .replace(/[^0-9K]/g, '')
    .slice(0, MAX_RUT_RAW_LENGTH);
  if (clean.length <= 1) return clean;

  const body = clean.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${body}-${clean.slice(-1)}`;
};

const RUT_VISIBLE_BODY_DIGITS = 3;

/** "12345678K" → "XX.XXX.678-K": visibles solo los últimos 3 dígitos del cuerpo y el verificador (CA-632). */
export const maskRut = (rut: string): string => {
  const parts = splitRut(rut);
  if (!parts) return rut;

  const firstVisibleIndex = parts.body.length - RUT_VISIBLE_BODY_DIGITS;
  let bodyIndex = -1;

  return formatRut(rut)
    .split('')
    .map((char) => {
      if (!/\d/.test(char)) return char;
      bodyIndex += 1;
      if (bodyIndex >= parts.body.length) return char;
      return bodyIndex < firstVisibleIndex ? 'X' : char;
    })
    .join('');
};
